import { prospectAllowsWidget } from "@logiful/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_DASHBOARD, WIDGET_KEYS, WIDGETS, widgetAllowed, type DashboardLayout, type WidgetKey } from "@logiful/domain";
import { AlertTriangle, ArrowDown, ArrowRight, ArrowUp, CalendarClock, GripVertical, LayoutGrid, Package, Plus, RotateCcw, ShoppingCart, Truck, Users, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { CalendarCard } from "@/components/CalendarCard";
import { NewsCard } from "@/components/NewsCard";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, REACH_STATUS_LABEL, type ModuleKey } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { ActionSummary } from "./ActionItems";
import { intlLocale, t, tn, msg } from "@/lib/i18n";
import { fmtMoney } from "@/lib/format";

type Item = DashboardLayout["widgets"][number];
const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");

/** breiter Bildschirm (zweispaltige Übersicht) */
function useWide() {
  const q = "(min-width: 1280px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
}

/** Welche Widgets darf diese Person sehen? (Leserecht, gebuchte Module; Logiful sieht alles) */
function useAllowed() {
  const { tenant, me } = useTenant();
  const may = usePermission();
  return useMemo(() => {
    const has = (m: string, f: string) => me.user.isStaff || !!tenant.modules?.[m as ModuleKey]?.includes(f);
    // Interessent: nur Kacheln freigegebener Bereiche (sonst sperrt die API die Daten)
    const prospect = me.user.isStaff ? null : tenant.prospect;
    return new Set(WIDGET_KEYS.filter((k) => widgetAllowed(k, (a) => may(a, "view"), has) && (!prospect || prospectAllowsWidget(prospect, k))));
  }, [tenant, me, may]);
}

export function DashboardPage() {
  const { tenantId, tenant, role } = useTenant();
  const qc = useQueryClient();
  const allowed = useAllowed();
  const q = useQuery({ queryKey: ["dashboard", tenantId], queryFn: () => api.dashboard.get(tenantId) });
  const [draft, setDraft] = useState<Item[] | null>(null);
  const editing = draft !== null;
  const layout = (q.data?.layout ?? DEFAULT_DASHBOARD).widgets.filter((w) => allowed.has(w.id));
  const isAdmin = canManage(role);

  const done = (msg: string) => { toast.success(msg); setDraft(null); void qc.invalidateQueries({ queryKey: ["dashboard", tenantId] }); };
  const saveMine = useMutation({ mutationFn: (w: Item[]) => api.dashboard.save(tenantId, { widgets: w }), onSuccess: () => done("Übersicht gespeichert"), onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }) });
  const saveTeam = useMutation({ mutationFn: (w: Item[]) => api.dashboard.saveTeam(tenantId, { widgets: w }), onSuccess: () => done("Als Team-Standard gespeichert – gilt für alle ohne eigene Anordnung"), onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }) });
  const reset = useMutation({ mutationFn: () => api.dashboard.reset(tenantId), onSuccess: () => done(q.data?.team ? "Auf Team-Standard zurückgesetzt" : "Auf Standard zurückgesetzt"), onError: (e) => toast.error("Nicht zurückgesetzt", { description: errMsg(e) }) });

  const hour = new Date().getHours();
  const greeting = hour < 11 ? "Guten Morgen" : hour < 18 ? "Hallo" : "Guten Abend";

  return (
    <div className="pb-16">
      <PageHeader
        title={editing ? t("Übersicht anpassen") : `${greeting} 👋`}
        description={editing ? t("Widgets hinzufügen, entfernen, verschieben und Breite wählen.") : t("Das passiert gerade bei {name}.", { name: tenant.name })}
        actions={!editing ? <Button icon={<LayoutGrid className="size-4" />} onClick={() => setDraft(layout)} disabled={q.isLoading}>{t("Anpassen")}</Button> : undefined}
      />
      <div className="px-4 md:px-8">
        {q.isLoading ? <Skeleton className="h-64" /> : editing ? (
          <LayoutEditor
            items={draft}
            onChange={setDraft}
            allowed={allowed}
            source={q.data?.source ?? "default"}
            hasTeam={!!q.data?.team}
            isAdmin={isAdmin}
            busy={saveMine.isPending || saveTeam.isPending || reset.isPending}
            onSave={() => saveMine.mutate(draft)}
            onSaveTeam={() => { if (confirm("Diese Anordnung als Standard für das ganze Team speichern? Wer keine eigene Anordnung hat, sieht sie sofort.")) saveTeam.mutate(draft); }}
            onReset={() => reset.mutate()}
            onCancel={() => setDraft(null)}
          />
        ) : (
          <WidgetGrid items={layout} />
        )}
      </div>
    </div>
  );
}

/** Anzeige: breite Widgets über die volle Breite, schmale in zwei Spalten (Reihenfolge bleibt erhalten) */
function WidgetGrid({ items }: { items: Item[] }) {
  const wide = useWide();
  if (!items.length)
    return (
      <Card className="p-8 text-center text-[13px] text-fg-muted">{t("Keine Widgets ausgewählt – über „Anpassen“ welche hinzufügen.")}</Card>
    );
  if (!wide) return <div className="space-y-6">{items.map((w) => <Widget key={w.id} id={w.id} />)}</div>;
  // Abschnitte: jedes breite Widget für sich, dazwischen schmale abwechselnd links/rechts
  const sections: ({ full: Item } | { left: Item[]; right: Item[] })[] = [];
  for (const w of items) {
    if (w.size === "full") { sections.push({ full: w }); continue; }
    let last = sections[sections.length - 1];
    if (!last || "full" in last) { last = { left: [], right: [] }; sections.push(last); }
    const half = last as { left: Item[]; right: Item[] };
    (half.left.length <= half.right.length ? half.left : half.right).push(w);
  }
  return (
    <div className="space-y-6">
      {sections.map((s, i) => "full" in s ? <Widget key={s.full.id} id={s.full.id} /> : (
        <div key={i} className="grid grid-cols-2 gap-6 items-start">
          <div className="space-y-6 min-w-0">{s.left.map((w) => <Widget key={w.id} id={w.id} />)}</div>
          <div className="space-y-6 min-w-0">{s.right.map((w) => <Widget key={w.id} id={w.id} />)}</div>
        </div>
      ))}
    </div>
  );
}

function Widget({ id }: { id: WidgetKey }) {
  switch (id) {
    case "kpis": return <KpisWidget />;
    case "action_items": return <ActionSummary />;
    case "news": return <NewsCard showEmpty />;
    case "calendar": return <CalendarCard />;
    case "order_flow": return <OrderFlowWidget />;
    case "channels": return <ChannelsWidget />;
    case "reorder": return <ReorderWidget />;
    case "slow_movers": return <SlowMoversWidget />;
    case "inbounds": return <InboundsWidget />;
    case "production": return <ProductionWidget />;
    case "profit": return <ProfitWidget />;
  }
}

// ── Bearbeiten ──

function LayoutEditor(p: {
  items: Item[]; onChange: (w: Item[]) => void; allowed: Set<WidgetKey>; source: "user" | "team" | "default"; hasTeam: boolean; isAdmin: boolean; busy: boolean;
  onSave: () => void; onSaveTeam: () => void; onReset: () => void; onCancel: () => void;
}) {
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= p.items.length) return;
    const next = [...p.items];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x!);
    p.onChange(next);
  };
  const missing = WIDGET_KEYS.filter((k) => p.allowed.has(k) && !p.items.some((w) => w.id === k));
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface-2/50 px-3 py-2.5 text-[12.5px]">
        <span className="text-fg-muted">{p.source === "user" ? t("Ihr nutzt eine eigene Anordnung.") : p.source === "team" ? t("Ihr seht den Team-Standard.") : t("Ihr seht den Standard.")}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          {p.source === "user" && <Button size="sm" variant="ghost" icon={<RotateCcw className="size-3.5" />} disabled={p.busy} onClick={p.onReset}>{p.hasTeam ? t("Auf Team-Standard zurücksetzen") : t("Auf Standard zurücksetzen")}</Button>}
          <Button size="sm" onClick={p.onCancel} disabled={p.busy}>{t("Abbrechen")}</Button>
          {p.isAdmin && <Button size="sm" icon={<Users className="size-3.5" />} disabled={p.busy} onClick={p.onSaveTeam}>{t("Als Team-Standard")}</Button>}
          <Button size="sm" variant="primary" loading={p.busy} onClick={p.onSave}>{t("Für mich speichern")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {p.items.map((w, i) => {
          const def = WIDGETS[w.id];
          return (
            <div
              key={w.id}
              draggable
              onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); setOver(i); }}
              onDragLeave={() => setOver((o) => (o === i ? null : o))}
              onDrop={(e) => { e.preventDefault(); if (drag !== null) move(drag, i); setDrag(null); setOver(null); }}
              onDragEnd={() => { setDrag(null); setOver(null); }}
              className={cx(
                "rounded-xl border bg-surface p-3 flex items-start gap-2 transition-colors",
                w.size === "full" && "xl:col-span-2",
                over === i && drag !== i ? "border-ink ring-2 ring-ink/10" : "border-border",
                drag === i && "opacity-50",
              )}
            >
              <GripVertical className="size-4 text-fg-subtle mt-0.5 cursor-grab shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-[13.5px]">{t(def.title)}</p>
                <p className="text-[12px] text-fg-muted">{t(def.description)}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <div className="hidden sm:flex rounded-lg border border-border p-0.5 text-[11.5px]">
                  {(["half", "full"] as const).map((s) => (
                    <button key={s} onClick={() => p.onChange(p.items.map((x) => (x.id === w.id ? { ...x, size: s } : x)))} className={cx("px-2 h-6 rounded-md", w.size === s && "bg-surface-2 font-medium")}>{s === "half" ? t("Schmal") : t("Breit")}</button>
                  ))}
                </div>
                <button onClick={() => move(i, i - 1)} disabled={i === 0} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg disabled:opacity-30" aria-label={t("{title} nach oben", { title: def.title })}><ArrowUp className="size-3.5" /></button>
                <button onClick={() => move(i, i + 1)} disabled={i === p.items.length - 1} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg disabled:opacity-30" aria-label={t("{title} nach unten", { title: def.title })}><ArrowDown className="size-3.5" /></button>
                <button onClick={() => p.onChange(p.items.filter((x) => x.id !== w.id))} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-danger" aria-label={t("{title} entfernen", { title: def.title })}><X className="size-3.5" /></button>
              </div>
            </div>
          );
        })}
        {!p.items.length && <p className="text-[13px] text-fg-muted">{t("Noch keine Widgets – unten hinzufügen.")}</p>}
      </div>

      {missing.length > 0 && (
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-wide text-fg-subtle mb-2">{t("Hinzufügen")}</p>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {missing.map((k) => (
              <button key={k} onClick={() => p.onChange([...p.items, { id: k, size: WIDGETS[k].size }])} className="text-left rounded-xl border border-dashed border-border p-3 hover:border-border-strong hover:bg-surface-2/50 transition-colors">
                <p className="font-medium text-[13.5px] flex items-center gap-1.5"><Plus className="size-3.5 text-fg-subtle" /> {t(WIDGETS[k].title)}</p>
                <p className="text-[12px] text-fg-muted">{t(WIDGETS[k].description)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
      <p className="text-[12px] text-fg-subtle">{t("Ziehen zum Verschieben oder die Pfeile nutzen. Ihr seht nur Widgets, für die ihr Rechte habt und deren Modul gebucht ist.")}</p>
    </div>
  );
}

// ── Widgets ──

function KpisWidget() {
  const { tenantId } = useTenant();
  const may = usePermission();
  const seeOrders = may("orders", "view"), seeProducts = may("products", "view");
  const summary = useQuery({ queryKey: ["products-summary", tenantId], queryFn: () => api.products.summary(tenantId), enabled: seeProducts });
  // im Server über alle Aufträge gezählt (nicht aus einer begrenzten Liste)
  const orders = useQuery({ queryKey: ["orders-summary", tenantId], queryFn: () => api.orders.summary(tenantId), enabled: seeOrders, refetchInterval: 60_000 });
  const s = summary.data;
  const total = s ? Object.values(s.byStatus).reduce((a, b) => a + (b ?? 0), 0) : 0;
  const blocked = s?.byStatus.invalid ?? 0;
  const o = orders.data;
  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {seeOrders && <>
        <Kpi label={t("Aufträge (24 h)")} value={o ? fmtNumber(o.last24h.placed) : null} sub={t("{v} bereits versendet", { v: fmtNumber(o?.last24h.shipped ?? 0) })} icon={<ShoppingCart className="size-4" />} to="/orders" />
        <Kpi label={t("Zurückgehaltene Aufträge")} value={o ? fmtNumber(o.onHold) : null} sub={o?.onHold ? t("warten auf Klärung") : t("alles frei")} tone={o?.onHold ? "warning" : "success"} icon={<AlertTriangle className="size-4" />} to="/orders?status=on_hold" />
      </>}
      {seeProducts && <>
        <Kpi label={t("Datenqualität")} value={s ? `${s.averageCompleteness}%` : null} sub={t("{total} Artikel", { total: fmtNumber(total) })} icon={<Package className="size-4" />} to="/products" progress={s?.averageCompleteness} />
        <Kpi label={t("Blockierte Artikel")} value={s ? fmtNumber(blocked) : null} sub={blocked ? t("gehen nicht ins Lager") : t("alle übertragbar")} tone={blocked ? "danger" : "success"} icon={<Truck className="size-4" />} to="/products?validation=invalid" />
      </>}
    </div>
  );
}

function OrderFlowWidget() {
  const { tenantId } = useTenant();
  const orders = useQuery({ queryKey: ["orders-summary", tenantId], queryFn: () => api.orders.summary(tenantId), refetchInterval: 60_000 });
  return (
    <Card className="p-5">
      <h2 className="font-semibold mb-4">{t("Auftragsfluss")}</h2>
      {!orders.data ? <Skeleton className="h-28" /> : <OrderFlow counts={orders.data.flow} />}
    </Card>
  );
}

function ChannelsWidget() {
  const { tenantId } = useTenant();
  const channels = useQuery({ queryKey: ["channels", tenantId], queryFn: () => api.channels.list(tenantId) });
  return (
    <Card className="p-5">
      <div className="flex items-center mb-3">
        <h2 className="font-semibold">{t("Shops")}</h2>
        <Link to="/channels" className="ml-auto text-[12.5px] text-fg-muted hover:text-fg">{t("Alle")}</Link>
      </div>
      <ul className="space-y-2">
        {(channels.data ?? []).map((c) => (
          <li key={c.id} className="flex items-center gap-3">
            <span className={cx("size-2 rounded-full shrink-0", c.status === "active" ? "bg-success" : c.status === "pending" ? "bg-warning" : "bg-danger")} />
            <span className="min-w-0">
              <span className="block font-medium text-[13px] truncate">{c.name}</span>
              <span className="block text-[11.5px] text-fg-subtle truncate">{c.externalAccount}</span>
            </span>
            <span className="ml-auto shrink-0 text-[12px] text-fg-muted">{c.status === "active" ? t("Sync {v}", { v: fmtRelative(c.lastSyncAt) }) : <Badge tone="danger">{t("getrennt")}</Badge>}</span>
          </li>
        ))}
        {channels.data && !channels.data.length && <li className="text-[13px] text-fg-muted">{t("Noch kein Shop verbunden.")}</li>}
      </ul>
    </Card>
  );
}

function ReorderWidget() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["reach", tenantId, "widget"], queryFn: () => api.stock.reach(tenantId, { filter: "action", sort: "urgency", dir: "asc", page: 1, pageSize: 6 }) });
  const d = q.data;
  return (
    <Card className="p-5">
      <div className="flex items-center mb-3">
        <h2 className="font-semibold">{t("Nachbestellen")}</h2>
        {d && <span className="ml-2 text-[12.5px] text-fg-subtle">{d.counts.out} {t("ausverkauft ·")} {d.counts.critical} {t("kritisch ·")} {d.counts.reorder} {t("bald")}</span>}
        <Link to="/stock?view=reach" className="ml-auto text-[12.5px] text-fg-muted hover:text-fg">{t("Alle")}</Link>
      </div>
      {q.isLoading ? <Skeleton className="h-24" /> : !d?.items.length ? <p className="text-[13px] text-fg-muted">{t("Nichts nachzubestellen – alles reicht über Lieferzeit und Puffer hinaus.")}</p> : (
        <ul className="space-y-2">
          {d.items.map((r) => (
            <li key={r.productId} className="flex items-center gap-3 text-[13px]">
              <span className={cx("size-2 rounded-full shrink-0", r.status === "reorder" ? "bg-warning" : "bg-danger")} />
              <span className="min-w-0 flex-1"><span className="block truncate font-medium">{r.name}</span><span className="block text-[11.5px] text-fg-subtle font-mono">{r.sku}</span></span>
              <span className="shrink-0 text-right">
                <span className="block text-[12px]">{REACH_STATUS_LABEL[r.status]}</span>
                {r.suggested > 0 && <span className="block text-[11.5px] text-fg-subtle tabular-nums">{t("Vorschlag")} {fmtNumber(r.suggested)}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SlowMoversWidget() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["slow-movers", tenantId, "widget"], queryFn: () => api.stock.slowMovers(tenantId, { filter: "action", sort: "capital", page: 1, pageSize: 4 }) });
  const d = q.data;
  const eur = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString(intlLocale(), { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
  return (
    <Card className="p-5">
      <div className="flex items-center mb-3">
        <h2 className="font-semibold">{t("Ladenhüter")}</h2>
        <Link to="/stock?view=slow" className="ml-auto text-[12.5px] text-fg-muted hover:text-fg">{t("Alle")}</Link>
      </div>
      {q.isLoading ? <Skeleton className="h-24" /> : !d ? null : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div><p className="text-[22px] font-semibold tabular-nums">{fmtNumber(d.counts.dead)}</p><p className="text-[11.5px] text-fg-muted">{t("ohne Absatz")}</p></div>
            <div><p className="text-[22px] font-semibold tabular-nums">{fmtNumber(d.counts.slow)}</p><p className="text-[11.5px] text-fg-muted">{t("Langsamdreher")}</p></div>
            <div><p className="text-[22px] font-semibold tabular-nums">{eur(d.summary.excessCapital)}</p><p className="text-[11.5px] text-fg-muted">{t("Kapital im Überschuss")}</p></div>
          </div>
          <ul className="space-y-1.5">
            {d.items.map((r) => (
              <li key={r.productId} className="flex items-center gap-3 text-[13px]">
                <span className="min-w-0 flex-1 truncate">{r.name}</span>
                <span className="shrink-0 text-[12px] text-fg-muted tabular-nums">{r.lastSaleDays == null ? t("nie verkauft") : t("vor {lastSaleDays} T.", { lastSaleDays: r.lastSaleDays })}</span>
                <span className="shrink-0 w-20 text-right tabular-nums font-medium">{eur(r.excessCapital)}</span>
              </li>
            ))}
            {!d.items.length && <li className="text-[13px] text-fg-muted">{t("Keine Ladenhüter.")}</li>}
          </ul>
        </>
      )}
    </Card>
  );
}

function InboundsWidget() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["inbounds", tenantId, "widget"], queryFn: () => api.inbounds.list(tenantId, { status: "draft,announced,partially_received" }) });
  const list = (q.data ?? []).slice().sort((a, b) => (a.appointment ? `${a.appointment.date}T${a.appointment.from}` : a.expectedAt ?? "9999").localeCompare(b.appointment ? `${b.appointment.date}T${b.appointment.from}` : b.expectedAt ?? "9999")).slice(0, 5);
  return (
    <Card className="p-5">
      <div className="flex items-center mb-3">
        <h2 className="font-semibold">{t("Offene Anlieferungen")}</h2>
        {q.data && <span className="ml-2 text-[12.5px] text-fg-subtle">{q.data.length}</span>}
        <Link to="/inbounds" className="ml-auto text-[12.5px] text-fg-muted hover:text-fg">{t("Alle")}</Link>
      </div>
      {q.isLoading ? <Skeleton className="h-24" /> : !list.length ? <p className="text-[13px] text-fg-muted">{t("Keine offenen Anlieferungen.")}</p> : (
        <ul className="space-y-2.5">
          {list.map((i) => {
            const exp = i.lines.reduce((n, l) => n + l.quantityExpected, 0);
            const rec = i.lines.reduce((n, l) => n + l.quantityReceived, 0);
            return (
              <li key={i.id} className="text-[13px]">
                <div className="flex items-center gap-2">
                  <Link to={`/inbounds?q=${encodeURIComponent(i.inboundNumber)}`} className="font-medium hover:underline truncate">{i.inboundNumber}</Link>
                  <span className="text-fg-subtle truncate">{i.supplier ?? ""}</span>
                  <span className="ml-auto shrink-0 text-[12px] text-fg-muted">
                    {i.appointment ? <span className="inline-flex items-center gap-1 text-info"><CalendarClock className="size-3.5" />{new Date(`${i.appointment.date}T12:00:00Z`).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" })} {i.appointment.from}</span>
                      : i.expectedAt ? t("erwartet {v}", { v: new Date(i.expectedAt).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" }) }) : t("ohne Termin")}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-1 flex-1 rounded-full bg-surface-2 overflow-hidden"><div className={cx("h-full rounded-full", rec > 0 ? "bg-warning" : "bg-border-strong")} style={{ width: `${exp ? Math.min(100, (rec / exp) * 100) : 0}%` }} /></div>
                  <span className="text-[11.5px] text-fg-subtle tabular-nums">{fmtNumber(rec)} / {fmtNumber(exp)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Kpi({ label, value, sub, icon, to, tone, progress }: { label: string; value: string | null; sub: string; icon: ReactNode; to: string; tone?: "success" | "warning" | "danger"; progress?: number }) {
  return (
    <Link to={to} className="group">
      <Card className="p-4 h-full transition-shadow group-hover:shadow-md group-hover:border-border-strong">
        <div className="flex items-center gap-2 text-fg-muted text-[12.5px]">
          <span className="text-fg-subtle">{icon}</span>
          {t(label)}
          <ArrowRight className="size-3.5 ml-auto opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
        </div>
        <div className="mt-2 text-[28px] font-semibold tracking-tight tabular-nums">{value ?? <Skeleton className="h-8 w-16" />}</div>
        {progress !== undefined && (
          <div className="mt-1 h-1 rounded-full bg-surface-2 overflow-hidden">
            <div className={cx("h-full rounded-full", progress >= 90 ? "bg-success" : progress >= 70 ? "bg-warning" : "bg-danger")} style={{ width: `${progress}%` }} />
          </div>
        )}
        <div className={cx("mt-1 text-[12px]", tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : tone === "success" ? "text-success" : "text-fg-subtle")}>{t(sub)}</div>
      </Card>
    </Link>
  );
}

function OrderFlow({ counts }: { counts: { received: number; on_hold: number; submitted: number; shipped: number } }) {
  const steps = [
    { key: "received", label: msg("Eingang"), to: "/orders?status=ready" },
    { key: "on_hold", label: msg("Zurückgehalten"), to: "/orders?status=on_hold" },
    { key: "submitted", label: msg("Beim Lager"), to: "/orders?status=submitted" },
    { key: "shipped", label: msg("Versendet (24 h)"), to: "/orders?status=shipped&period=1" },
  ] as const;
  const max = Math.max(1, ...Object.values(counts));
  return (
    <div className="grid grid-cols-4 gap-2">
      {steps.map((s) => (
        <Link key={s.key} to={s.to} className="group">
          <div className="h-20 flex items-end rounded-lg bg-surface-2 overflow-hidden">
            <div
              // gleiche Farben wie die Status-Badges der Auftragsliste: Eingang neutral, zurückgehalten Warnung, beim Lager Info, versendet Erfolg
              className={cx("w-full rounded-t-md transition-all", { received: "bg-fg-subtle/50", on_hold: "bg-warning/80", submitted: "bg-info/80", shipped: "bg-success/80" }[s.key], "group-hover:opacity-80")}
              style={{ height: `${Math.max(6, (counts[s.key] / max) * 100)}%` }}
            />
          </div>
          <div className="mt-1.5 text-[18px] font-semibold tabular-nums">{counts[s.key]}</div>
          <div className="text-[12px] text-fg-muted">{t(s.label)}</div>
        </Link>
      ))}
    </div>
  );
}

const PRODUCTION_LABEL: Record<string, string> = { requested: "Beauftragt", accepted: "Angenommen", in_production: "In Produktion" };

/** Produktion: offene Aufträge, das Wichtigste zuerst (Muster zur Freigabe, fehlende Komponenten) */
function ProductionWidget() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["production-orders", tenantId, "all"], queryFn: () => api.production.orders(tenantId, "all") });
  const open = (q.data?.items ?? []).filter((o) => ["requested", "accepted", "in_production"].includes(o.status));
  const short = (o: (typeof open)[number]) => !!o.components?.holdReasons.some((r) => r.startsWith("Fehlbestand"));
  const rank = (o: (typeof open)[number]) => (o.sampleStatus === "submitted" ? 0 : short(o) ? 1 : 2);
  const list = open.slice().sort((a, b) => rank(a) - rank(b)).slice(0, 5);
  return (
    <Card className="p-5">
      <div className="flex items-center mb-3">
        <h2 className="font-semibold">{t("Produktion")}</h2>
        <Link to="/production" className="ml-auto text-[12.5px] text-fg-muted hover:text-fg">{t("Alle")}</Link>
      </div>
      {q.isLoading ? <Skeleton className="h-24" /> : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div><p className="text-[22px] font-semibold tabular-nums">{fmtNumber(open.length)}</p><p className="text-[11.5px] text-fg-muted">{t("offen")}</p></div>
            <div><p className="text-[22px] font-semibold tabular-nums">{fmtNumber(open.filter((o) => o.status === "in_production").length)}</p><p className="text-[11.5px] text-fg-muted">{t("in Produktion")}</p></div>
            <div><p className={cx("text-[22px] font-semibold tabular-nums", open.some(short) && "text-warning")}>{fmtNumber(open.filter(short).length)}</p><p className="text-[11.5px] text-fg-muted">{t("warten auf Komponenten")}</p></div>
          </div>
          <ul className="space-y-1.5">
            {list.map((o) => (
              <li key={o.id}>
                <Link to={`/production?open=${o.id}`} className="flex items-center gap-3 text-[13px] hover:underline">
                  <span className="shrink-0 font-medium tabular-nums">{o.number}</span>
                  <span className="min-w-0 flex-1 truncate text-fg-muted">{fmtNumber(o.quantity)} × {o.product.name}</span>
                  {o.sampleStatus === "submitted" ? <Badge tone="warning">{t("Muster zur Freigabe")}</Badge> : short(o) ? <Badge tone="danger">{t("Komponenten fehlen")}</Badge> : <Badge>{t(PRODUCTION_LABEL[o.status] ?? o.status)}</Badge>}
                </Link>
              </li>
            ))}
            {!list.length && <li className="text-[13px] text-fg-muted">{t("Keine offenen Produktionsaufträge")}</li>}
          </ul>
        </>
      )}
    </Card>
  );
}

/** Profit Center: letzte 30 Tage (netto) */
function ProfitWidget() {
  const { tenantId } = useTenant();
  const day = (d: Date) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
  const to = day(new Date());
  const from = day(new Date(Date.now() - 29 * 86_400_000));
  const q = useQuery({ queryKey: ["profit", tenantId, from, to, ""], queryFn: () => api.profit.report(tenantId, { from, to }), staleTime: 60_000, retry: false });
  const r = q.data;
  const pct = (v: number | null | undefined) => (v == null ? "—" : `${v.toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} %`);
  const top = (r?.products ?? []).filter((p) => p.contributionMinor != null).slice().sort((a, b) => (b.contributionMinor ?? 0) - (a.contributionMinor ?? 0)).slice(0, 3);
  return (
    <Card className="p-5">
      <div className="flex items-center mb-3">
        <h2 className="font-semibold">{t("Profit Center")}</h2>
        <span className="ml-2 text-[12px] text-fg-subtle">{t("letzte 30 Tage, netto")}</span>
        <Link to="/profit" className="ml-auto text-[12.5px] text-fg-muted hover:text-fg">{t("Details")}</Link>
      </div>
      {q.isLoading ? <Skeleton className="h-24" /> : !r ? <p className="text-[13px] text-fg-muted">{t("Noch keine Auswertung verfügbar.")}</p> : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div><p className="text-[20px] font-semibold tabular-nums">{fmtMoney(r.totals.revenueNetMinor)}</p><p className="text-[11.5px] text-fg-muted">{t("Umsatz")}</p></div>
            <div><p className={cx("text-[20px] font-semibold tabular-nums", r.totals.contributionMinor < 0 ? "text-danger" : "text-success")}>{fmtMoney(r.totals.contributionMinor)}</p><p className="text-[11.5px] text-fg-muted">{t("Deckungsbeitrag")}</p></div>
            <div><p className="text-[20px] font-semibold tabular-nums">{pct(r.totals.marginPct)}</p><p className="text-[11.5px] text-fg-muted">{tn(r.totals.orders, "{n} Bestellung", "{n} Bestellungen")}</p></div>
          </div>
          <ul className="space-y-1.5">
            {top.map((p) => (
              <li key={p.sku} className="flex items-center gap-3 text-[13px]">
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                <span className="shrink-0 text-[12px] text-fg-muted tabular-nums">{pct(p.marginPct)}</span>
                <span className="shrink-0 w-24 text-right tabular-nums font-medium">{fmtMoney(p.contributionMinor ?? 0)}</span>
              </li>
            ))}
          </ul>
          {r.missingCost.length > 0 && <p className="mt-2 text-[12px] text-warning">{tn(r.missingCost.length, "Für {n} Artikel fehlt der Einstandspreis", "Für {n} Artikel fehlen Einstandspreise")}</p>}
        </>
      )}
    </Card>
  );
}
